import { useEscape } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { ReactNode } from "react";

import { Button, buttonClasses } from "../Button/Button";
import CloseButton from "../SpecificButtons/CloseButton/CloseButton";

export type DialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Usually a string; a node when the heading carries an icon inline. */
  title: ReactNode;
  /** Body content. */
  children: ReactNode;
  /** Empty string hides the action; both hidden hides the row (info dialog). */
  negLabel?: string;
  posLabel?: string;
  onNegative?: () => void;
  onPositive?: () => void;
  /** Greys out the positive action, e.g. until a choice is made. */
  posDisabled?: boolean;
  /**
   * When false, the positive action leaves closing to `onPositive`: for a
   * dialog that stays up while its save is in flight.
   */
  closeOnPositive?: boolean;
  /** "subtle" renders the negative action as underlined text. */
  negVariant?: "secondary" | "subtle";
  /** Destructive confirm: the positive action renders as red underlined text. */
  danger?: boolean;
  /**
   * Makes the positive action a link rather than a button. For destinations a
   * client-side navigation can't reach, such as another deploy, where an <a>
   * and a full page load are the point. `onPositive` still runs, for tracking.
   */
  posHref?: string;
  /** Opens `posHref` in a new tab and closes the dialog, instead of leaving. */
  posHrefNewTab?: boolean;
  /** Max width of the dialog surface. */
  width?: number;
  /** When false, backdrop click and Escape don't close — only the X and actions. */
  dismissable?: boolean;
  /** When false, no X: the actions are the only way out. */
  showClose?: boolean;
  /** "dimmed" keeps the page readable behind, for a dialog raised mid-action. */
  backdrop?: "blurred" | "dimmed";
};

/**
 * Centered modal over a dimmed, blurred backdrop. Same header/close/actions
 * contract as Popover, but controlled from outside (like BottomSheet).
 */
export const Dialog = ({
  isOpen,
  onClose,
  title,
  children,
  negLabel,
  posLabel,
  onNegative,
  onPositive,
  posDisabled = false,
  closeOnPositive = true,
  negVariant = "secondary",
  danger = false,
  posHref,
  posHrefNewTab = false,
  width = 440,
  dismissable = true,
  showClose = true,
  backdrop = "blurred",
}: DialogProps) => {
  const { t } = useTranslation();
  useEscape(() => isOpen && dismissable && onClose());
  if (!isOpen) return null;

  const neg = negLabel ?? t("cancel");
  const pos = posLabel ?? t("confirm");
  const hasNeg = neg.trim() !== "";
  const hasPos = pos.trim() !== "";
  const posVariant = danger ? "subtle" : "primary";
  const posClass =
    danger && !posDisabled
      ? "text-negative-foreground decoration-negative-foreground hover:text-red-300 hover:decoration-red-300"
      : undefined;

  return (
    <div
      onClick={() => dismissable && onClose()}
      className={cn(
        "fixed inset-0 z-60 flex items-center justify-center p-6",
        backdrop === "blurred"
          ? "bg-black/70 backdrop-blur-[3px]"
          : "bg-black/50",
        "transition-opacity duration-150 ease-out opacity-100 starting:opacity-0",
      )}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: width }}
        className={cn(
          "relative w-full rounded-2xl border border-neutral-700 bg-neutral-800 p-6",
          "shadow-[0_30px_80px_rgba(0,0,0,0.6)]",
          "transition-all duration-200 ease-out",
          "translate-y-0 scale-100 opacity-100",
          "starting:translate-y-2.5 starting:scale-[0.97] starting:opacity-0",
        )}
      >
        {/*
          Without a title there is no header row to sit in, so the close button
          is pulled out of the flow and pinned to the corner — otherwise an
          empty text block still claimed a row and left the X floating in a band
          of its own. Same branch Popover already makes.
        */}
        {title ? (
          <div className="mb-2 flex items-start justify-between gap-4">
            <div className="text-sm font-semibold leading-[1.25] text-foreground desktop:text-lg">
              {title}
            </div>
            {showClose && (
              <div className="-mr-1.5 -mt-1.5 shrink-0">
                <CloseButton onClick={onClose} />
              </div>
            )}
          </div>
        ) : (
          showClose && (
            <div className="absolute right-4 top-4 z-10">
              <CloseButton onClick={onClose} />
            </div>
          )
        )}
        <div className="text-sm leading-relaxed text-foreground-muted">
          {children}
        </div>
        {/* The positive action takes the remaining width; the negative only its own. */}
        {(hasNeg || hasPos) && (
          <div className="mt-6 flex justify-end gap-2.5">
            {hasNeg && (
              <Button
                variant={negVariant}
                size="medium"
                className="px-5"
                onClick={() => {
                  onNegative?.();
                  onClose();
                }}
              >
                {neg}
              </Button>
            )}
            {hasPos &&
              (posHref ? (
                // No onClose: the page is leaving, and closing first would show
                // a frame of whatever is behind the dialog.
                <a
                  href={posHref}
                  target={posHrefNewTab ? "_blank" : undefined}
                  rel={posHrefNewTab ? "noopener" : undefined}
                  onClick={() => {
                    onPositive?.();
                    if (posHrefNewTab) onClose();
                  }}
                  className={buttonClasses({
                    variant: posVariant,
                    stretch: true,
                    className: posClass,
                  })}
                >
                  {pos}
                </a>
              ) : (
                <Button
                  variant={posVariant}
                  size="medium"
                  stretch
                  className={posClass}
                  disabled={posDisabled}
                  onClick={() => {
                    onPositive?.();
                    if (closeOnPositive) onClose();
                  }}
                >
                  {pos}
                </Button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
